"use client";

import React, { Suspense, useEffect, useRef } from "react";
import markdownStyles from "../../styles/markdown-styles.module.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { useLightbox } from "@/components/context/lightbox-context";
import dynamic from "next/dynamic";
import type { Components } from "react-markdown";
import clsx from "clsx";
import { cn } from "@/lib/utils";

// Dynamically import SyntaxHighlighter to reduce initial bundle size
const SyntaxHighlighter = dynamic(() =>
  import("@/components/syntax").then((mod) => mod.SyntaxHighlighter),
);

// We don't actually need the motion import for Web Animations API
// const motion = dynamic(() => import("@/lib/motion").then(mod => mod.motion), {
//   ssr: false
// });

const ClickableImage = ({ src, alt }) => {
  const { setIsLightboxOpen, setLightboxSrc, setClickPosition } = useLightbox();

  const handleClick = (e: React.MouseEvent<HTMLImageElement>) => {
    // Capture the click position
    const rect = e.currentTarget.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;

    setClickPosition({ x, y });
    setLightboxSrc(src);
    setIsLightboxOpen(true);
  };

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      onClick={handleClick}
      src={src}
      alt={alt}
      className="my-0 h-auto w-full rounded-md hover:cursor-zoom-in!"
    />
  );
};

// Function to pre-process content and replace translation syntax with the TranslationMorph component
function preprocessMarkdown(content: string): string {
  // Replace ^original|translation^ with a custom component render placeholder
  let counter = 0;
  return content.replace(
    /\^([^|^]+)(?:\|([^^]+))?\^/g,
    (_, original, translation) => {
      const trans = translation || original;
      const id = counter++;
      // Create a placeholder with unique ID that we'll replace with our component
      return `<span data-morph-id="${id}" data-original="${original}" data-translation="${trans}"></span>`;
    },
  );
}

export function Markdown({
  content,
  balance = false,
  pretty = false,
  ...props
}) {
  const markdownRef = useRef<HTMLDivElement>(null);

  // Client-side effect to add text morphing behavior to translation spans
  useEffect(() => {
    if (!markdownRef.current) return;

    const morphSpans = markdownRef.current.querySelectorAll(
      "span[data-morph-id]",
    );

    morphSpans.forEach((span) => {
      const original = span.getAttribute("data-original");
      const translation = span.getAttribute("data-translation");

      if (!original || !translation) return;

      // Create a clean environment for our motion component
      span.innerHTML = "";
      (span as HTMLElement).style.display = "inline-block";
      (span as HTMLElement).style.position = "relative";
      (span as HTMLElement).style.cursor = "help";
      (span as HTMLElement).style.borderBottom =
        "1px dotted var(--foreground, #888)";

      // Create the DOM element for the text
      const textElement = document.createElement("span");
      textElement.textContent = original;
      span.appendChild(textElement);

      // Track the state
      let showing = "original";

      // Add hover effect
      span.addEventListener("mouseenter", () => {
        if (showing === "original") {
          // Animate out
          textElement.animate(
            [
              { opacity: 1, transform: "translateY(0)" },
              { opacity: 0, transform: "translateY(-4px)" },
            ],
            { duration: 150, easing: "ease-out", fill: "forwards" },
          );

          // Wait for animation to complete
          setTimeout(() => {
            textElement.textContent = translation;

            // Animate in
            textElement.animate(
              [
                { opacity: 0, transform: "translateY(4px)" },
                { opacity: 1, transform: "translateY(0)" },
              ],
              { duration: 150, easing: "ease-out", fill: "forwards" },
            );

            showing = "translation";
          }, 150);
        }
      });

      span.addEventListener("mouseleave", () => {
        if (showing === "translation") {
          // Animate out
          textElement.animate(
            [
              { opacity: 1, transform: "translateY(0)" },
              { opacity: 0, transform: "translateY(4px)" },
            ],
            { duration: 150, easing: "ease-out", fill: "forwards" },
          );

          // Wait for animation to complete
          setTimeout(() => {
            textElement.textContent = original;

            // Animate in
            textElement.animate(
              [
                { opacity: 0, transform: "translateY(-4px)" },
                { opacity: 1, transform: "translateY(0)" },
              ],
              { duration: 150, easing: "ease-out", fill: "forwards" },
            );

            showing = "original";
          }, 150);
        }
      });
    });

    return () => {
      morphSpans.forEach((span) => {
        span.removeEventListener("mouseenter", () => {});
        span.removeEventListener("mouseleave", () => {});
      });
    };
  }, []);

  // Component for rendering code blocks with syntax highlighting
  const CodeBlock = ({ language, value }) => {
    return (
      <div className="relative rounded-xl">
        <Suspense
          fallback={
            <div className="animate-pulse rounded-xl bg-stone-100 p-4 dark:bg-stone-800">
              Loading code...
            </div>
          }
        >
          <SyntaxHighlighter language={language} code={value} showCopyButton />
        </Suspense>
      </div>
    );
  };

  // Pre-process the content to handle translations
  const processedContent = preprocessMarkdown(content);

  return (
    <div
      ref={markdownRef}
      className={clsx(
        markdownStyles.markdown,
        balance && "text-balance",
        pretty && "text-pretty",
        "prose dark:prose-invert prose-h2:mt-8 prose-img:rounded-lg prose-blockquote:!border-stone-200 dark:prose-blockquote:!border-stone-700 prose-blockquote:!text-stone-600 dark:prose-blockquote:!text-stone-400",
        props.className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={
          {
            a: ({ node, ...a }) => {
              const id = Math.random().toString(36).substring(7);

              if (a.href.includes("mailto:"))
                return (
                  <a key={id} {...a}>
                    {a.children}
                  </a>
                );

              if (a.href.startsWith("/")) {
                return (
                  <a key={id} {...a}>
                    {a.children}
                  </a>
                );
              }

              return (
                <a key={id} target="_blank" rel="noopener noreferrer" {...a}>
                  {a.children}
                </a>
              );
            },
            img: (node) => {
              return <ClickableImage src={node.src} alt={node.alt} />;
            },
            code: ({ inline, className, children, ...props }: any) => {
              const match = /language-(\w+)/.exec(className || "");
              const childRegex = String(children).replace(/\n$/, "");
              return !inline && match ? (
                <CodeBlock language={match[1]} value={childRegex} />
              ) : (
                <code
                  className={clsx(
                    "rounded-md bg-stone-100 px-1 py-0.5 dark:bg-stone-800",
                    className,
                  )}
                  {...props}
                >
                  {children}
                </code>
              );
            },
          } as Components
        }
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
}
